import { MediaMetaData } from "@/types";

/**
 * Simple codec validator using blacklist approach
 * Rejects known browser-incompatible codecs, lets everything else through
 * Browser will naturally validate remaining codecs at playback time
 */
export class CodecValidator {
    /**
     * Video codecs that browsers cannot play in HTML5 video elements
     * These are legacy/proprietary codecs that require conversion
     */
    private static readonly UNSUPPORTED_VIDEO_CODECS = [
        'mpeg4',    // MPEG-4 Part 2 (DivX, Xvid)
        'divx',
        'xvid',
        'msmpeg4',  // Microsoft MPEG-4
        'wmv',      // Windows Media Video
        'wmv1', 'wmv2', 'wmv3',
        'vc1',      // VC-1 codec
        'rv',       // RealVideo
        'sorenson', // Sorenson codec
        'cinepak',
        'indeo',
        'svq',      // Sorenson Video Quantizer
    ];

    /**
     * Audio codecs that browsers cannot play
     */
    private static readonly UNSUPPORTED_AUDIO_CODECS = [
        'wma',      // Windows Media Audio
        'wma1', 'wma2',
        'wmapro',
        'cook',     // RealAudio COOK
        'sipr',     // RealAudio SIPR
        'ac3',      // Dolby Digital (limited browser support)
        'eac3',     // Dolby Digital Plus
        'dts',      // DTS audio
        'truehd',   // Dolby TrueHD
        'ape',      // Monkey's Audio
        'alac',     // Apple Lossless (limited support)
    ];

    /**
     * Validate codecs against blacklist
     * Throws error for known unsupported codecs, otherwise allows through
     *
     * @throws Error if codec is known to be unsupported by browsers
     */
    public async validateCodecSupport(metadata: MediaMetaData): Promise<void> {
        const videoTrack = metadata.videos?.[0];
        const audioTrack = metadata.audio?.[0];

        if (!videoTrack && !audioTrack) {
            throw new Error('No video or audio tracks found in media file');
        }

        const videoCodec = videoTrack?.codec;
        const audioCodec = audioTrack?.codec;

        console.log('[CodecValidator] Checking codecs:', { videoCodec, audioCodec });

        this.checkBlacklist(videoCodec, audioCodec);

        console.log('[CodecValidator] Codecs passed blacklist check - browser will validate at playback');
    }

    /**
     * Check if codecs are in the blacklist of known unsupported codecs
     * Throws error immediately for codecs that definitely won't work
     */
    private checkBlacklist(
        videoCodec: string | null | undefined,
        audioCodec: string | null | undefined
    ): void {
        if (videoCodec) {
            const codecLower = videoCodec.toLowerCase();
            const isUnsupported = CodecValidator.UNSUPPORTED_VIDEO_CODECS.some(
                unsupported => codecLower.includes(unsupported)
            );

            if (isUnsupported) {
                throw new Error(
                    `Video codec "${videoCodec}" cannot be played by browsers. ` +
                    `This is a legacy/proprietary codec not supported by HTML5 video. ` +
                    `Please convert your video to H.264 format before uploading.`
                );
            }
        }

        if (audioCodec) {
            const codecLower = audioCodec.toLowerCase();
            const isUnsupported = CodecValidator.UNSUPPORTED_AUDIO_CODECS.some(
                unsupported => codecLower.includes(unsupported)
            );

            if (isUnsupported) {
                throw new Error(
                    `Audio codec "${audioCodec}" cannot be played by browsers. ` +
                    `Please convert your audio to AAC or MP3 format before uploading.`
                );
            }
        }
    }
}
